import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { SuspenseContent } from '@e3dc-react/shell/Elements/Suspense'
import { useHeaderBreadcrumbs } from '@e3dc-react/shell/Libs/Header'
import { useIsWebviewMode } from 'src/Hooks'
import { useContentView } from '../hooks'
import { PAGE_CONTENT_ID, PAGE_CONTENT_LOADING_ID } from 'src/Libs/qa/ids'

interface StyleProps {
  withTabs: boolean
  headerHeight?: number
  withBreadcrumbs: boolean
  isContentHeaderHidden: boolean
  isNavigationHidden: boolean
  isWebView: boolean
}

const MOBILE_HEADER_HEIGTH_WITH_TABS_AND_BREADCRUMBS = 108
const MOBILE_HEADER_HEIGHT_WITH_TABS = 87
const MOBILE_HEADER_HEIGHT_WITH_BREADCRUMBS = 81
const MOBILE_HEADER_HEIGHT = 59

const getMobileHeaderHeight = (withTabs: boolean, withBreadcrumbs: boolean, isContentHeaderHidden: boolean): number => {
  if (isContentHeaderHidden) {
    return 0
  }
  if (withTabs && withBreadcrumbs) {
    return MOBILE_HEADER_HEIGTH_WITH_TABS_AND_BREADCRUMBS
  }
  if (withTabs) {
    return MOBILE_HEADER_HEIGHT_WITH_TABS
  }
  if (withBreadcrumbs) {
    return MOBILE_HEADER_HEIGHT_WITH_BREADCRUMBS
  }
  return MOBILE_HEADER_HEIGHT
}

const useStyles = makeStyles<StyleProps>()((theme, props) => {
  /* required padding to top: margin of header bar to top + header bar height + height of tabs + 12px padding */
  const defaultHeaderHeight = theme.shape.header.top + theme.shape.header.mobileHeight + (props.withTabs ? 19 : 0) + 2
  const contentTopPadding: number = (props.headerHeight ?? defaultHeaderHeight) + theme.space(2)
  const mobileHeaderHeight = getMobileHeaderHeight(props.withTabs, props.withBreadcrumbs, props.isContentHeaderHidden)

  return {
    mainContent: {
      height: '100%',
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      [theme.breakpoints.down('lg')]: {
        paddingTop: contentTopPadding + theme.space(2),
      },
      [theme.breakpoints.up('md')]: {
        paddingLeft: '4px',
        paddingTop: contentTopPadding,
        marginTop: theme.spacing(2),
      },
      [theme.breakpoints.down('md')]: {
        paddingTop: mobileHeaderHeight + theme.space(2),
        paddingBottom: props.isWebView && !props.isNavigationHidden ? `calc(56px + ${theme.spacing(2)})` : theme.spacing(2),
        width: '100%',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        /* Helps the paddings blend with rest of the content, which has white background color. */
        backgroundColor: props.isContentHeaderHidden ? theme.palette.common.white : 'inherit',
      },
    },
  }
})

interface Props {
  withTabs: boolean
  headerHeight: number
  children?: React.ReactNode
}

/**
 * Wraps the apps content and adds some translation functionality based on certain conditions
 */
const ContentWrapper: React.FC<Props> = ({ withTabs, headerHeight, children }) => {
  const { breadcrumbs } = useHeaderBreadcrumbs()
  const { isContentHeaderHidden, isNavigationHidden } = useContentView()
  const isWebView = useIsWebviewMode()
  const { classes } = useStyles({
    withTabs,
    headerHeight,
    withBreadcrumbs: breadcrumbs.length > 0,
    isContentHeaderHidden,
    isNavigationHidden,
    isWebView,
  })

  return (
    <main className={classes.mainContent} data-qa={PAGE_CONTENT_ID}>
      <SuspenseContent fallbackDataQa={PAGE_CONTENT_LOADING_ID}>{children}</SuspenseContent>
    </main>
  )
}

export default ContentWrapper
